import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Spinner } from '../components/ui';
import { useT } from '../i18n';
import { AuthDiagnostics } from './AuthDiagnostics';
import { useAuth } from './AuthProvider';
import {
  clearPostAuthRedirect,
  isFavoriteTabLandingPath,
  rememberPostAuthRedirect,
  resolvePostAuthTarget,
  syncFavoriteTabMirror,
  targetFromLocation,
} from './postAuthRedirect';

// Nach dieser Zeit im Loading-Zustand zeigen wir statt des Spinners eine
// Fehlermeldung mit Retry — sonst haengt die App bei Netzwerk-/CORS-Fehlern
// optisch "fuer immer" (Netzwerkfehler ist bewusst KEIN Auto-Logout).
const LOADING_TIMEOUT_MS = 12000;

// Die Pfade, auf denen ein frisch eingeloggter Nutzer landet: '/' (Index-
// Redirect) und JEDER der drei Lieblings-Tab-Landeplaetze (/chats, /dashboard,
// /discover — worauf Index-Redirect, Login- und Onboarding-Flow springen).
// Nur von hier aus loesen wir ein gemerktes Deep-Link-Ziel ein — sonst wuerde
// ein spaeterer, ganz normaler Besuch dieser Seiten gekapert.
//
// W1-C/Karte C Risiko 5: Diese Liste MUSS alle Landeplaetze kennen. Kennt sie
// nur '/dashboard', bleibt der Merker beim Landen auf /chats liegen und kapert
// den naechsten regulaeren Besuch des Lieblings-Tabs.
function isPostAuthLandingRoute(pathname: string): boolean {
  return pathname === '/' || isFavoriteTabLandingPath(pathname);
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const { status, user, lastError } = useAuth();
  const { t } = useT();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (status !== 'loading') {
      setTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setTimedOut(true), LOADING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  // Task 4.2: Der Merker gilt fuer genau einen Auth-Umweg. Sobald der Nutzer
  // eingeloggt irgendwo ausserhalb der Landeplaetze steht (weil wir ihn unten
  // hingeschickt haben oder weil er selbst weitergeklickt hat), ist er erledigt.
  const onLandingRoute = isPostAuthLandingRoute(location.pathname);
  useEffect(() => {
    if (status === 'authed' && !onLandingRoute) clearPostAuthRedirect();
  }, [status, onLandingRoute]);

  // W1-C: Der Server ist die Wahrheit fuer den Lieblings-Tab — sobald /me da
  // ist, zieht der clientPrefs-Spiegel nach. RequireAuth ist die einzige Stelle,
  // die JEDE authentifizierte Route umschliesst, also greift das auch, wenn der
  // Nutzer per Deep-Link irgendwo anders einsteigt. `syncFavoriteTabMirror`
  // schreibt nur bei gueltigem Serverwert UND echter Aenderung.
  const favoriteTab = user?.favorite_tab;
  useEffect(() => {
    if (status !== 'authed') return;
    syncFavoriteTabMirror({ favorite_tab: favoriteTab });
  }, [status, favoriteTab]);

  if (status === 'loading') {
    return (
      <main
        aria-busy={!timedOut}
        style={{
          display: 'grid',
          minHeight: '100dvh',
          placeItems: 'center',
        }}
      >
        {timedOut ? (
          <div role="alert" style={{ display: 'grid', gap: '16px', justifyItems: 'center', padding: '24px', textAlign: 'center' }}>
            <p style={{ margin: 0 }}>{t('auth.js.connection_error')}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t('chat.retry')}
            </Button>
            <AuthDiagnostics error={lastError} />
          </div>
        ) : (
          <Spinner size="md" />
        )}
      </main>
    );
  }
  // Unauth-Einstieg ist der Onboarding-Flow (Tindra-Chat mit Login/Register/Reset inline),
  // NICHT der klassische /login-Screen. /login bleibt als Route fuer Deep-Links erreichbar.
  if (status === 'anonymous') {
    // Task 4.2: Ziel merken, BEVOR wir den Unangemeldeten wegschicken. Der Onboarding-Chat
    // navigiert nach Login/Register hart auf /dashboard und verliert dabei
    // Router-State und Query — nur der Merker ueberlebt diesen Umweg. Der Schreib-
    // Vorgang steht bewusst im Render und nicht in einem Effect: <Navigate/>
    // haengt uns im selben Commit ab, ein Passive-Effect wuerde verworfen.
    rememberPostAuthRedirect(targetFromLocation(location));
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }
  // Task 4.2: Deep-Link einloesen — nur vom Landeplatz aus und nur einmal
  // (der Effect oben raeumt den Merker am Ziel wieder ab).
  const pendingTarget = onLandingRoute ? resolvePostAuthTarget(location) : null;
  if (pendingTarget && pendingTarget !== location.pathname) {
    return <Navigate to={pendingTarget} replace />;
  }
  // Die Startup-Gates liegen zentral ueber dem Router. RequireAuth entscheidet
  // nur noch ueber Auth und Redirects, damit hier kein zweites Modal unabhaengig
  // vom Profil- oder Push-Gate entstehen kann.
  return children;
}
